import { useState, useEffect } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, FilePlus2, Archive, CalendarClock, Bell, Banknote } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatEuro, formatData } from "@/lib/api";
import InstallButton from "@/components/InstallButton";
import NotificheButton from "@/components/NotificheButton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const NAV = [
  { to: "/", label: "Cruscotto", icon: LayoutDashboard, end: true },
  { to: "/nuovo", label: "Nuovo Assegno", icon: FilePlus2 },
  { to: "/archivio", label: "Archivio", icon: Archive },
  { to: "/scadenze", label: "Scadenze", icon: CalendarClock },
];

const Layout = () => {
  const [notifiche, setNotifiche] = useState([]);
  const location = useLocation();

  const carica = () => api.notifiche().then(setNotifiche).catch(() => {});

  useEffect(() => {
    carica();
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex bg-[#F9F8F6]">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-white border-r border-[#E5E2DC] hidden md:flex flex-col fixed h-screen">
        <div className="px-6 py-7 border-b border-[#E5E2DC] flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#1C3F35] flex items-center justify-center">
            <Banknote className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <div>
            <p className="font-heading font-bold text-[#1A1C18] leading-tight">AssegniTrack</p>
            <p className="text-xs text-[#5C5F58]">Gestione & Tracciabilità</p>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.to === "/" ? "dashboard" : item.to.slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-[#1C3F35] text-white"
                    : "text-[#5C5F58] hover:bg-[#F2F0EB] hover:text-[#1A1C18]"
                }`
              }
            >
              <item.icon className="w-[18px] h-[18px]" strokeWidth={1.8} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 m-4 rounded-xl bg-[#F2F0EB] text-xs text-[#5C5F58] leading-relaxed">
          Promemoria automatici 3 giorni prima della scadenza di ogni assegno.
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 md:ml-64 flex flex-col min-h-screen">
        <header className="h-16 bg-white border-b border-[#E5E2DC] flex items-center justify-between px-6 sticky top-0 z-20">
          <div className="md:hidden flex items-center gap-2 font-heading font-bold">
            <Banknote className="w-5 h-5 text-[#1C3F35]" /> AssegniTrack
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
          <NotificheButton />
          <InstallButton />
          <Popover>
            <PopoverTrigger asChild>
              <button
                data-testid="notifiche-bell"
                className="relative w-10 h-10 rounded-lg hover:bg-[#F2F0EB] flex items-center justify-center transition-colors"
              >
                <Bell className="w-5 h-5 text-[#5C5F58]" strokeWidth={1.8} />
                {notifiche.length > 0 && (
                  <span
                    data-testid="notifiche-badge"
                    className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D45D3C] text-white text-[10px] font-bold flex items-center justify-center"
                  >
                    {notifiche.length}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0" data-testid="notifiche-panel">
              <div className="px-4 py-3 border-b border-[#E5E2DC]">
                <p className="font-heading font-semibold text-sm">Promemoria scadenze</p>
                <p className="text-xs text-[#5C5F58]">Assegni in scadenza entro 3 giorni</p>
              </div>
              <div className="max-h-80 overflow-auto">
                {notifiche.length === 0 ? (
                  <p className="p-6 text-center text-sm text-[#5C5F58]">Nessuna scadenza imminente 🎉</p>
                ) : (
                  notifiche.map((n) => (
                    <div key={n.id} className="px-4 py-3 border-b border-[#F2F0EB] last:border-0">
                      <div className="flex justify-between items-start gap-2">
                        <div>
                          <p className="text-sm font-medium">{n.banca || "Banca n.d."}</p>
                          <p className="text-xs text-[#5C5F58]">N° {n.numero_assegno || "—"} · {formatEuro(n.valore)}</p>
                        </div>
                        <span className="text-xs font-semibold text-[#B84223] whitespace-nowrap">
                          {n.giorni_alla_scadenza < 0
                            ? `Scaduto da ${Math.abs(n.giorni_alla_scadenza)}g`
                            : n.giorni_alla_scadenza === 0
                            ? "Scade oggi"
                            : `Tra ${n.giorni_alla_scadenza}g`}
                        </span>
                      </div>
                      <p className="text-xs text-[#5C5F58] mt-1">Scadenza: {formatData(n.data_scadenza)}</p>
                    </div>
                  ))
                )}
              </div>
            </PopoverContent>
          </Popover>
          </div>
        </header>

        <main className="flex-1 p-6 md:p-8 lg:p-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
            >
              <Outlet context={{ refreshNotifiche: carica }} />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};

export default Layout;
